import axios from 'axios'
import { get } from './base'
import { useQuery } from '@tanstack/vue-query'
import moment from 'moment/moment'
import {
  AggregateChartResponse,
  ChartResponse,
  SensorSummary,
  TriggerConfiguration,
} from '@/models/sensors-dto'
import { convertToCamelCase } from '@/models/utils/casing'
import { computed, Ref, unref } from 'vue'

export function getSensorsForLocation(id: string | number, signal?: AbortSignal) {
  const source = axios.CancelToken.source()

  const sensors = get<SensorSummary[]>(`/api/v2/sensors?locationId=${id}`, {
    cancelToken: source.token,
  }).then((res) => convertToCamelCase(res.data))

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return sensors
}

type MaybeRef<T> = Ref<T> | T

export function useSensorsForLocationQuery(
  locationId?: MaybeRef<string | number | null | undefined>,
) {
  const computedId = computed(() => unref(locationId))
  const queryKey = computed(() => ['sensors-at', computedId.value])
  const enabled = computed(() => {
    return computedId.value !== null && computedId.value !== undefined
  })

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => getSensorsForLocation(computedId.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
  })

  return query
}

export function getSensorReadings(
  sensorIds: string[],
  windowStart: Date,
  windowEnd: Date,
  signal?: AbortSignal,
) {
  const source = axios.CancelToken.source()

  const searchParams = new URLSearchParams()

  for (const i in sensorIds) {
    searchParams.append('sensorIds', sensorIds[i])
  }

  searchParams.append('windowStart', windowStart.toISOString())
  searchParams.append('windowEnd', windowEnd.toISOString())

  const sensors = get<ChartResponse>(
    `/api/v3/sensors/charts/tanklevelsensor?${searchParams.toString()}`,
    {
      cancelToken: source.token,
    },
  ).then((res) => convertToCamelCase(res.data))

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return sensors
}

export function useSensorReadingsChartData(
  sensorIds?: MaybeRef<string[] | null | undefined>,
  windowStart?: MaybeRef<Date | null | undefined>,
  windowEnd?: MaybeRef<Date | null | undefined>,
) {
  const ids = computed(() => unref(sensorIds))
  const start = computed(() => unref(windowStart))
  const end = computed(() => unref(windowEnd))

  const queryKey = computed(() => ['sensor-readings', sensorIds, start, end])
  const enabled = computed(() => {
    return (
      ids.value !== null &&
      ids.value!.length > 0 &&
      start.value !== undefined &&
      end.value !== undefined
    )
  })

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => getSensorReadings(ids.value!, start.value!, end.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
  })

  return query
}

export function getAggregateReadings(
  sensorDeviceIds: string[],
  organizationId: string,
  windowStart: Date,
  windowEnd: Date,
  signal?: AbortSignal,
) {
  const source = axios.CancelToken.source()

  const searchParams = new URLSearchParams()

  for (const id of sensorDeviceIds) {
    searchParams.append('sensorDeviceIds', id)
  }

  searchParams.append('organizationId', organizationId)
  searchParams.append('windowStart', windowStart.toISOString())
  searchParams.append('windowEnd', windowEnd.toISOString())

  const readings = get<AggregateChartResponse>(
    `/api/v3/triggers/charts/aggregateTankLevel?${searchParams.toString()}`,
    {
      cancelToken: source.token,
    },
  ).then((res) => convertToCamelCase(res.data))

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return readings
}

export function useAggregateReadingsChartData(
  sensorDeviceIds?: MaybeRef<string[] | null | undefined>,
  organizationId?: MaybeRef<string | null | undefined>,
  windowStart?: MaybeRef<Date | null | undefined>,
  windowEnd?: MaybeRef<Date | null | undefined>,
) {
  const ids = computed(() => unref(sensorDeviceIds))
  const organization = computed(() => unref(organizationId))
  const start = computed(() => unref(windowStart))
  const end = computed(() => unref(windowEnd))

  const queryKey = computed(() => ['aggregate-sensor-readings', ids, organization, start, end])
  const enabled = computed(() => {
    return (
      ids.value != null &&
      ids.value.length > 0 &&
      organization.value != null &&
      start.value != null &&
      end.value != null
    )
  })

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getAggregateReadings(ids.value!, organization.value!, start.value!, end.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
    retry: (failureCount, error) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined
      if (status != null && status >= 400 && status < 500) return false
      return failureCount < 1
    },
  })

  return query
}

export function getTriggerConfiguration(triggerId: string, signal?: AbortSignal) {
  const source = axios.CancelToken.source()

  const configuration = get<TriggerConfiguration>(`/api/v3/triggers/${triggerId}/configuration`, {
    cancelToken: source.token,
  }).then((res) => convertToCamelCase(res.data))

  signal?.addEventListener('abort', () => {
    source.cancel('Query was cancelled by TanStack Query')
  })

  return configuration
}

export function useTriggerConfigurationQuery(triggerId?: MaybeRef<string | null | undefined>) {
  const id = computed(() => unref(triggerId))

  const queryKey = computed(() => ['trigger-configuration', id.value])
  const enabled = computed(() => id.value != null && id.value !== '')

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => getTriggerConfiguration(id.value!, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
    enabled,
  })

  return query
}
